import React from 'react';
import { useScreenTheme } from '@/providers/ScreenThemeStateProvider/EventScreenThemeContext';
import { StageQR } from './StageQR';
import { StageEngagementTitle } from '../../engagements/StageEngagementTitle';
import { ThemeOverride } from '@/providers/ThemeOverride';
import { EngagementMode } from '@/types/screen';
import {
  raffleStatusFromEngagement,
  shouldHideRaffleJoinCta,
  shouldMuteRaffleJoinCta,
} from '@/engagements/raffle/stage/raffleChrome';
import { useStageFreeformPresentation } from '@/engagements/slides/stageFreeformPresentationContext';
import { FreeformStageJoinCta } from '@/engagements/slides/freeform/FreeformStageJoinCta';

interface StageChromeProps {
  children: React.ReactNode;
  name: string;
}

export function StageChrome({ children, name }: StageChromeProps) {
  const { state } = useScreenTheme();
  const { isFreeformSlide, showQr } = useStageFreeformPresentation();

  const hasActiveEngagement =
    state.engagementMode === EngagementMode.Guide ||
    Boolean(
      state.engagementMode === EngagementMode.Actual && state.activeEngagement,
    );

  const raffleStatus = raffleStatusFromEngagement(state.activeEngagement);
  const muteJoin = shouldMuteRaffleJoinCta(raffleStatus);
  const hideJoin = shouldHideRaffleJoinCta(raffleStatus);

  if (isFreeformSlide) {
    return (
      <ThemeOverride theme="dark">
        <div data-name={name} className="relative h-full min-h-0 w-full">
          <div
            data-name={`${name}_CENTER`}
            className="relative z-10 h-full min-h-0 w-full"
          >
            {children}
          </div>
          {hasActiveEngagement && !hideJoin && showQr ? (
            <FreeformStageJoinCta muteJoin={muteJoin} />
          ) : null}
        </div>
      </ThemeOverride>
    );
  }

  return (
    <ThemeOverride theme="dark">
      <div data-name={name} className="flex h-full min-h-0">
        <div
          data-name={`${name}_CENTER`}
          className={`z-10 mx-[2vw] my-[2vh] h-[calc(100%-4vh)] min-h-0 flex-1 ${hasActiveEngagement ? 'px-[4vw] pb-[3vh] pt-[7vh]' : ''} `}
        >
          {children}
        </div>
        <div
          data-name={`${name}_SIDEBAR`}
          className={`flex-0 relative z-20 flex h-full flex-col justify-end transition-all duration-1000 ${
            hideJoin
              ? 'pointer-events-none w-0 overflow-hidden p-0 opacity-0'
              : `w-[21vw] p-[2vh] ${
                  hasActiveEngagement ? 'opacity-100' : 'opacity-0'
                }`
          }`}
        >
          <div
            data-name="STAGE_JOIN_CTA"
            data-muted={muteJoin ? 'true' : 'false'}
            className={`flex w-full flex-col items-center justify-end gap-3 transition-all duration-700 ${
              muteJoin ? 'opacity-[0.38] grayscale' : 'opacity-100'
            }`}
          >
            <StageEngagementTitle />
            <StageQR hideCta={muteJoin} mutedAppearance={muteJoin} />
          </div>
        </div>
      </div>
    </ThemeOverride>
  );
}
